import { useMutation, useQuery } from "@tanstack/react-query";
import { axiosReq } from "@/lib/axios";
import { CalendarEvent, CreatePostFormType, GeneratedTheme, SocialPostType, ThemeEvent } from "../types";
import { queryClient } from "@/lib/react-query" 
import { captureApiError } from "@/lib/sentry-fn";
import posthog from "posthog-js";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import { useContext } from "react";
import { AuthContext, AuthContextType } from "@/features/app-layout/context";

// 1-> review
// 5 => marked

export const useGetThemes = (page: number, date: string, status: number | undefined) => {

  const { name } = useContext<AuthContextType>(AuthContext)

  const request = async (): Promise<{
    introduction: string,
    images: string[],
    data: ThemeEvent[],
    has_next_page: boolean
  }> => {
    const response = await axiosReq.post('/api/approval/get-pending-themes/', { page: page, end_date: date, status: status, user: name })
    return response.data
  }

  const { data: themes } = useQuery({
    queryKey: ["themes", page, date, status],
    queryFn: request,
    enabled: !!status
  })

  return { themes }
}


export const useGetPendingThemes = (page: number, date: string, status: number | undefined) => {

  const { name } = useContext<AuthContextType>(AuthContext)

  const request = async (): Promise<{
    introduction: string,
    images: string[],
    data: ThemeEvent[],
    has_next_page: boolean
  }> => {
    const response = await axiosReq.post('/api/approval/get-lookback-themes/',
      {
        page: page,
        start_date: format(subDays(new Date(), 1), "yyyy-MM-dd"),
        end_date: format(subDays(new Date(), 30), "yyyy-MM-dd"),
        username: name
      })
    return response.data
  }

  const { data: pastThemes } = useQuery({
    queryKey: ["pending-themes", page, date],
    queryFn: request,
    enabled: !status
  })

  return { pastThemes }
}

export const useGetMaxDate = () => {
  const request = async (): Promise<{
    max_end_date: string
  }> => {
    const response = await axiosReq.get('/api/calendar/max-end-date/')
    return response.data
  }

  const { data: maxEndDate } = useQuery({
    queryKey: ["max-end-date"],
    queryFn: request
  })

  return { maxEndDate }
}

export const useGetThemeDetails = (themeId: number | undefined, storyId: number | undefined) => {

  const { username } = useContext<AuthContextType>(AuthContext)

  const request = async (): Promise<{
    data: {
      event_id: number,
      event: string,
      story_id: number | null,
      posting_type: "Post" | "Campaign",
      status: number,
      content: SocialPostType[]
    },
    introduction: string
  }> => {
    const response = await axiosReq.post('/api/approval/get-single-theme/', {
          event_id: themeId,
    user: username,
    story_id: storyId ? storyId : null
    })
    return response.data
  }

  const { data: themeDetailData } = useQuery({
    queryKey: ["theme-details", themeId, storyId],
    queryFn: request,
    enabled: !!themeId
  })
  return { themeDetailData }
}

export const useRegenerateEvent = (setLoading: () => void) => {
  const request = async ({ event_id, prompt, story_id }: { event_id: number, prompt: string, story_id?: number }): Promise<{
    message: string,
    post: {
      post_id: number,
      platform: string,
      text: string,
      image_video_recommendations: string,
      hashtags: string[],
      cta: string[],
      date_of_posting: string,
      time_of_posting: string,
    }[]
  }> => {

    const data = story_id ? {event_id, story_id, recommendation: prompt} : {event_id, recommendation: prompt}

    try {
      const response = await axiosReq.post('/api/approval/regenerate_posts/', data)
      return response.data
    } catch (error) {

      captureApiError(error, {
        component: "useRegenerateEvent",
        url: `${import.meta.env.VITE_API_URL}/api/approval/regenerate_posts/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/regenerate_posts/"
      })

      throw error
    }
  }


  const { mutate: regenerateEvent, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
      queryClient.invalidateQueries({ queryKey: ["themes"] })
      setLoading()
    }
  })

  return { regenerateEvent, status }
}

type UpdatePostBodyArgs = {
  post_id: number;
  post_content: string;
  hashtags: string[];
  post_date: string,
  post_time: string,
}

export const useUpdatePostBody = () => {

  const request = async (postBody: UpdatePostBodyArgs) => {
    const response = await axiosReq.post('/api/approval/post-save/', postBody)
    return response.data;
  }
  const { mutate: updatePostBody, isSuccess, isPending } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["theme-details"] });
      toast.success("Content updated")
    }
  })

  return { updatePostBody, isSuccess, isPending }
}

export const useSchedulePost = (onSchedule: (text: string) => void) => {

  const request = async ({ postId, img_ids }: { postId: number, img_ids: string[] | null }): Promise<{ introduction: string }> => {
    const response = await axiosReq.post('/api/approval/schedule_post/', { post_id: postId, image_ids: img_ids });
    return response.data
  }

  const { mutate: schedulePost } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      toast.success("Event Scheduled successfully")
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
      queryClient.invalidateQueries({ queryKey: ["themes"] })
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      if (data) {

        onSchedule(data.introduction)
      }
    }
  })

  return { schedulePost }
}


export const useCreateThemePosts = (onSchedule?: (data: number, name: string) => void) => {

  const request = async ({ formData, signal }: { formData: CreatePostFormType, signal: AbortSignal }): Promise<{ data: GeneratedTheme }> => {

    const data = {
      event_description: formData.event_description,
      activity: formData.activity,
      catalogue: "",
      event_time: formData.event_time,
      start_date: format(formData.event_date, 'yyyy-MM-dd'),
      end_date: formData.campiagn_timeline ? format(formData.campiagn_timeline, 'yyyy-MM-dd') : format(formData.event_date, 'yyyy-MM-dd'),   
    }


    try {
      const response = await axiosReq.post('/api/approval/create-post/', data, { signal });
      return response.data;
    } catch (error) {

      captureApiError(error, {
        component: "usePostCreateSocialPost",
        url: `${import.meta.env.VITE_API_URL}/api/approval/create-post/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/create-post/"
      })

      throw error
    }
  }

  const { mutate: createSocialPost, status, data: generatedTheme } = useMutation({
    mutationFn: (formData: CreatePostFormType) => {
      const controller = new AbortController()
      return request({ formData, signal: controller.signal })
    },
    onSuccess: (data) => {
      console.log("success");

      if (data) {
        posthog.capture('event created', { event_created: data.data.event_id })
        if (onSchedule) {
          onSchedule(data.data.event_id, data.data.event_name)
        }
      }
    }
  });

  return { createSocialPost, status, generatedTheme }

}

export type SuggestedEvent = {
  event_id: number;
  event_type: string;
  start_date: string;
  end_date: string;
  event_name: string;
  event_description: string;
  activity: "Campaign" | "Post";
  catalogue: string;
  event_time: string;
}

export const useGetPostSuggestions = () => {
  const request = async (): Promise<{ data: SuggestedEvent[] }> => {
    try {
      const response = await axiosReq.post('api/approval/create-post-history/');
      return response.data
    } catch (error) {

      captureApiError(error, {
        component: "useGetPostSuggestion",
        url: `${import.meta.env.VITE_API_URL}/api/approval/create-post-history/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/create-post-history/"
      })

      throw error
    }

  }

  const { data: suggestedEvents, isLoading } = useQuery({
    queryKey: ["suggested-events"],
    queryFn: request,
    gcTime: 0
  })

  return { suggestedEvents, isLoading }
}


export const useGetCalendarEvents = (startDate: Date, endDate: Date) => {

  const request = async ({ signal }: { signal: AbortSignal }): Promise<{
    data: CalendarEvent[]
  }> => {
    const data = {
      user_id: 2,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
      page: 1
    }
    const response = await axiosReq.post('/api/calendar/get-calendar/', data, { signal });
    return response.data;
  }
  const { data: calendarEvents, isLoading } = useQuery({
    queryKey: ["Events", startDate, endDate], // add startdata and endDate as parameter for queryKey
    queryFn: request,
  })
  return { calendarEvents, isLoading }
}

export const useGetCalendarFreq = () => {

  const requestFreq = async ({ signal }: { signal: AbortSignal }): Promise<{
    date: string,
    frequency: number,
  }[]> => {
    const response = await axiosReq.get('/api/calendar/calendar-heat-map/', { signal })
    return response.data;
  }

  const { data: calendarFreq, isLoading } = useQuery({
    queryKey: ["calendar-freq"],
    queryFn: requestFreq
  })

  return { calendarFreq, isLoading }
}

export const useGetPostTimeline = () => {

  const request = async ({ signal }: { signal: AbortSignal }): Promise<{
    timeline: {
      post_id: number,
      isPosted: boolean,
      post_title: string,
      posting_date: string,
      platform: string,
    }[]
  }> => {
    const response = await axiosReq.get('/api/approval/timeline/', { signal });
    return response.data
  }

  const { data: timeline, isLoading } = useQuery({
    queryKey: ["timeline"],
    queryFn: request
  })

  return { timeline, isLoading }
}

type MultiPlatformArgs = {
  post_id: number,
  platforms: string[]
}

export const useAddMultiPlatform = () => {

  const request = async ({
    post_id,
    platforms,
  }: MultiPlatformArgs): Promise<{
    new_post_ids: number[]
  }> => {
    const response = await axiosReq.post('/api/approval/multi-platform-post/', { post_id, platforms })
    return response.data
  }

  const { mutate: addPlatforms, status, isSuccess, data: posts } = useMutation({
    mutationFn: request
  })

  return { addPlatforms, status, isSuccess, posts }
}

export const useSaveImageTheme = () => {
  const request = async (data: { event_id: number, image_id: number, story_id: number }) => {
    const response = await axiosReq.post('/api/approval/save-image-post/', data);
    return response.data
  }

  const { mutate: saveImage, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      queryClient.invalidateQueries({ queryKey: ["socials-unreviewed"] })
      queryClient.invalidateQueries({ queryKey: ["themes"] })
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
    }
  })

  return { saveImage, status }
}

